import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "rizzui";
import { FieldError, Field, FormGroup } from "@steelyard/theme";
import { SelectField } from "../select-field";

/** Ported from apps/admin/components/assign-terms-form.tsx. All three fields required — no partial assignment. */
export type TierOption = { id: string; name: string; isDefault: boolean; isActive: boolean };

const numeric = (v: string) => Number.isFinite(Number(v));

const assignTermsSchema = z.object({
  tierId: z.string().trim().min(1, "Choose a tier"),
  creditLimit: z.string().trim().min(1, "Required").refine(numeric, "Enter a number"),
  paymentTermDays: z.string().trim().min(1, "Required").refine(numeric, "Enter a number"),
});
type AssignTermsValues = z.infer<typeof assignTermsSchema>;

export function AssignTermsForm({
  onSubmit,
  tiers,
  customerName,
  tradeTermsRequestId,
  currentOutstanding,
  current,
  error,
}: {
  onSubmit: (formData: FormData) => void | Promise<void>;
  tiers: readonly TierOption[];
  customerName: string;
  tradeTermsRequestId?: string;
  currentOutstanding?: string;
  /** The terms a B2B customer already has, so "Change tier, limit or term" starts from them. */
  current?: { tierId?: string | null; creditLimit?: number | string | null; paymentTermDays?: number | null };
  error?: unknown;
}) {
  const assignable = tiers.filter((tier) => !tier.isDefault && tier.isActive);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AssignTermsValues>({
    resolver: zodResolver(assignTermsSchema),
    defaultValues: {
      tierId: current?.tierId ?? "",
      creditLimit: current?.creditLimit == null ? "" : String(current.creditLimit),
      paymentTermDays: current?.paymentTermDays == null ? "" : String(current.paymentTermDays),
    },
  });

  const submit = handleSubmit(async (values) => {
    const formData = new FormData();
    formData.set("tierId", values.tierId);
    formData.set("creditLimit", values.creditLimit);
    formData.set("paymentTermDays", values.paymentTermDays);
    if (tradeTermsRequestId) formData.set("tradeTermsRequestId", tradeTermsRequestId);
    await onSubmit(formData);
  });

  return (
    <form onSubmit={submit} noValidate className="@container flex flex-col gap-5">
      {error ? <FieldError error={error} /> : null}

      <FormGroup title="Grant terms">
        <SelectField
          label="Tier"
          required
          disabled={assignable.length === 0}
          disabledReason={assignable.length === 0 ? "No tiers available — create an active, non-default tier first." : undefined}
          hint="Assigning any tier other than the Default Tier makes this customer B2B: unit rates instead of piece prices, from their next load."
          error={errors.tierId?.message}
          {...register("tierId")}
        >
          <option value="" disabled>
            {assignable.length === 0 ? "No tiers available" : "Choose a tier…"}
          </option>
          {assignable.map((tier) => (
            <option key={tier.id} value={tier.id}>
              {tier.name}
            </option>
          ))}
        </SelectField>

        <Field
          label="Credit limit"
          required
          inputMode="decimal"
          error={errors.creditLimit?.message}
          hint={
            currentOutstanding
              ? `${customerName} currently owes ${currentOutstanding}. A limit below that leaves them over it immediately and their next order is blocked.`
              : "In rupees. Required — a tier without a limit is credit that was never granted."
          }
          {...register("creditLimit")}
        />

        <Field
          label="Payment term (days)"
          required
          inputMode="numeric"
          hint="Days from invoice to due date. Required."
          error={errors.paymentTermDays?.message}
          {...register("paymentTermDays")}
        />
      </FormGroup>

      <div className="flex flex-col gap-3">
        <Button type="submit" disabled={isSubmitting}>
          Assign tier, limit and term
        </Button>
        <p className="text-muted-foreground text-xs">
          All three are set together. There is no way to give {customerName} a tier without a credit limit and a
          payment term, because that would be a B2B customer able to order on credit nobody granted.
        </p>
      </div>
    </form>
  );
}
